<template>
  <div class="receivables-page">
    <div class="receivables-page-header">
      <div class="summary" v-loading="summaryDataFetching">
        <div class="card-container">
          <el-card header="应收总额" shadow="never" class="summary-card purple">
            <div class="amount">
              <app-locale-string :value="summaryData.receivable_amount" :show-currency="true" />
            </div>
          </el-card>
          <el-card header="已收总额" shadow="never" class="summary-card green">
            <div class="amount">
              <app-locale-string :value="summaryData.received_amount" :show-currency="true" />
            </div>
          </el-card>
          <el-card header="应收余额" shadow="never" class="summary-card red">
            <div class="amount">
              <app-locale-string :value="summaryData.unreceived_receivable_amount" :show-currency="true" />
            </div>
          </el-card>
        </div>
      </div>
    </div>
    <div class="receivables-page-main">
      <bean-source-page
        ref="sourcePage"
        :type="type"
        :columns="columns"
        :form-columns="formColumns"
        :filter-columns="filterColumns"
        :actions="actions"
        :batch-actions="batchActions"
        :form="form"
        :title="$t('model.reports/receivable')"
        resource="reports/receivables"
        :action-buttons="[]"
        :transformIndexQueryParams="transformIndexQueryParams"
        :onFetchData="handleFetchData"
      >
      <template #action>
        <DialogLoading
          :visible="exportLoading"
          description="正在生成导出文件，预计需要 1-3 分钟，请等待"
          :title="`导出处理中`"
        />
        <el-dialog
          :visible.sync="showBuyerIdsDialog"
          title="买家账号详情"
          width="600px"
          :close-on-click-modal="false"
        >
          <div class="buyer-ids-content">
            <div class="buyer-ids-list">
              <el-tag
                v-for="(buyerId, index) in dialogBuyerIds"
                :key="index"
                style="margin-right: 8px; margin-bottom: 8px;"
              >
                {{ buyerId }}
              </el-tag>
            </div>
          </div>
          <div slot="footer" class="dialog-footer">
            <el-button @click="showBuyerIdsDialog = false">关闭</el-button>
          </div>
        </el-dialog>
        <el-dialog
          :visible.sync="showShopNamesDialog"
          title="店铺详情"
          width="600px"
          :close-on-click-modal="false"
        >
          <div class="shop-names-content">
            <div class="shop-names-list">
              <el-tag
                v-for="(shopName, index) in dialogShopNames"
                :key="index"
                style="margin-right: 8px; margin-bottom: 8px;"
              >
                {{ shopName }}
              </el-tag>
            </div>
          </div>
          <div slot="footer" class="dialog-footer">
            <el-button @click="showShopNamesDialog = false">关闭</el-button>
          </div>
        </el-dialog>
        <BillSendRecordsDialog
          :visible.sync="showBillSendRecordsDialog"
          :customer="currentBillSendRecordCustomer"
          @updated="handleBillSendRecordsUpdated"
        />
      </template>
      </bean-source-page>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import _ from 'lodash';
import DialogLoading from '@/components/dialog-loading';
import { exportFile } from '@/utils/export';
import BillSendRecordsDialog from './bill-send-records-dialog.vue';

@Component({
  components: {
    DialogLoading,
    BillSendRecordsDialog
  }
})
export default class Receivables extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}
  summaryData = {}
  summaryDataFetching = false;
  exportLoading = false;
  showBuyerIdsDialog = false;
  dialogBuyerIds = [];
  showShopNamesDialog = false;
  dialogShopNames = [];
  showBillSendRecordsDialog = false;
  currentBillSendRecordCustomer = null;

  transformIndexQueryParams(params = {}) {
    const { jushuitan_orders_created_range_date } = params;
    if (jushuitan_orders_created_range_date) {
      params.jushuitan_orders_created_gteq_date = jushuitan_orders_created_range_date[0];
      params.jushuitan_orders_created_lteq_date = jushuitan_orders_created_range_date[1];
      delete params.jushuitan_orders_created_range_date;
    }

    return _.pickBy(params, value => !_.isNil(value) && value !== '');
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  filterColumns() {
    return [
      {
        prop: 'id_eq',
        label: '客户',
        renderCell: {
          component: 'app-read-options',
          props: {
            clearable: true,
            url: 'customers/read_options',
            params: this.customerOptionsFilter,
            labelField: 'name',
            valueField: 'id',
          }
        }
      },
      {
        prop: 'user_id_eq',
        label: '业务员',
        renderCell: {
          component: 'app-read-options',
          props: {
            clearable: true,
            url: 'users/read_options',
            params: this.userOptionsFilter,
            labelField: 'screen_name',
            valueField: 'id',
          }
        }
      },
      {
        prop: 'shop_id_eq',
        label: '店铺',
        renderCell: {
          component: 'app-read-options',
          props: {
            clearable: true,
            url: 'jushuitan_shops/read_options',
            params: { per_page: 100 },
            labelField: 'shop_name',
            valueField: 'shop_id',
            idInField: 'shop_id_in',
          }
        }
      },
      {
        prop: 'is_even',
        label: '是否结清',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: [
              { value: 1, label: '是' },
              { value: 0, label: '否' }
            ]
          }
        }
      },
      {
        prop: 'jushuitan_orders_created_range_date',
        label: '聚水潭订单下单日期范围',
        renderCell: {
          component: 'datePicker',
          props: {
            valueFormat: 'yyyy-MM-dd',
            type: 'daterange',
            rangeSeparator: '至',
            clearable: true,
          },
          hint: '筛选此时间段有销售订单下单的客户',
        }
      },
      {
        prop: 'sales_orders_product_type_eq',
        label: '销售订单产品类型',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: _.map(this.$t('salesOrder.productTypes'), (value, key) => ({ value: key, label: value }))
          }
        }
      },
      {
        prop: 'user_team_id_eq',
        label: '团队/小组',
        renderCell: {
          component: 'app-team-tree-select',
        }
      },
    ];
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  scopeColumns() {
    return [];
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  columns() {
    return [
      {
        prop: 'shop_names',
        label: '店铺',
        width: 180,
        renderCell: (h, { props: { value } }) => {
          if (value.length) {
            const displayValue = value.slice(0, 5);
            const hasMore = value.length > 5;

            return (
              <div>
                {_.map(displayValue, (object) => <el-tag style={{ 'margin-right': '3px' }}>{object}</el-tag>)}
                {hasMore && (
                  <el-button
                    type="text"
                    size="mini"
                    style={{ 'margin-left': '5px', 'color': '#409EFF' }}
                    onClick={() => this.showShopNamesDetail(value)}
                  >
                    查看更多({value.length})
                  </el-button>
                )}
              </div>
            );
          } else {
            return <span>/</span>
          }
        }
      },
      {
        prop: 'name',
        label: '客户',
        width: 150,
      },
      {
        prop: 'shop_buyer_id',
        label: '买家账号',
        width: 150,
      },
      {
        prop: 'user_screen_name',
        label: '业务员',
        width: 120,
      },
      {
        prop: 'is_even',
        label: '是否结清',
        width: 100,
        renderCell: 'bool'
      },
      {
        prop: 'overdue',
        label: '是否逾期',
        width: 100,
        renderCell: (h, { props: { value } }) => {
          return value ? <el-tag type="danger">是</el-tag> : <el-tag type="success">否</el-tag>;
        }
      },
      {
        prop: 'overdue_amount',
        label: '逾期金额',
        width: 120,
        renderCell: {
          component: 'app-locale-string',
          props: {
            color: '#F56C6C'
          }
        }
      },
      {
        prop: 'receivable_amount',
        label: '应收总额',
        width: 120,
        renderCell: {
          component: 'app-locale-string',
        }
      },
      {
        prop: 'received_amount',
        label: '已收总额',
        width: 120,
        renderCell: {
          component: 'app-locale-string',
        }
      },
      {
        prop: 'unreceived_receivable_amount',
        label: '应收余额',
        width: 120,
        renderCell: {
          component: 'app-locale-string',
        }
      },
      {
        prop: 'latest_bill_send_record_sent_at',
        label: '最新账单发送时间',
        width: 120,
        renderCell: {
          component: 'time',
        }
      },
      {
        prop: 'latest_bill_send_record_confirmed_at',
        label: '最新账单确认时间',
        width: 120,
        renderCell: {
          component: 'time',
        }
      },
      // {
      //   prop: 'sales_order_count',
      //   label: '销售订单数量',
      //   width: 120,
      // },
    ];
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   * @param {Object} params.data - 当前表单数据
   * @param {Function} params.change - 调用此方法同步表单数据 change({ field: 'new value' })
   */
  formColumns() {
    return [
      {
        prop: 'id',
        label: '编号',
        renderCell: {
          component: 'input',
          required: true
        }
      }
    ];
  }

   /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }) {
    return [
      {
        component: 'routeButton',
        props: {
          label: '查看销售订单',
          to: { name: 'sales_orders.index', query: { customer_id_eq: row.id } }
        }
      },
      {
        component: 'routeButton',
        props: {
          label: '查看聚水潭订单',
          to: { name: 'jushuitan_orders.index', query: { customer_id_eq: row.id } }
        }
      },
      {
        component: 'routeButton',
        props: {
          label: '查看收款记录',
          to: { name: 'payments.index', query: { customer_id_eq: row.id } }
        }
      },
      {
        component: 'button',
        props: {
          label: '账单发送记录',
          type: 'primary',
        },
        on: {
          click: () => this.handleOpenBillSendRecordsDialog(row)
        }
      },
      {
        component: 'button',
        props: {
          label: '导出明细',
          type: 'primary',
        },
        on: {
          click: () => this.handleExportDetail(row)
        }
      }
    ].filter(Boolean);
  }

  /**
   * @param {Object} scope -表格 scope 数据
   * @param {Object} scope.selection - 表格已选择行数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  batchActions({ selection }) {
    return [
      () => <el-button type="primary" onClick={() => this.handleExport(selection)}>{selection.length ? `导出选中${selection.length}项` : '导出所有项（如有筛选，则导出已筛选数据）'}</el-button>
    ];
  }

  async handleExport(selection) {
    this.exportLoading = true;
    try {
      let params = {};
      if (selection.length) {
        params = { id_in: selection.map(item => item.id) };
      } else {
        params = this.transformIndexQueryParams({ ...this.$route.query, page: undefined, per_page: undefined });
      }
      await exportFile('reports/receivables/summary_xlsx', params);
    } finally {
      this.exportLoading = false;
    }
  }

  async handleExportDetail({ id }) {
    this.exportLoading = true;
    try {
      await exportFile(`reports/receivables/${id}/detail_xlsx`);
    } finally {
      this.exportLoading = false;
    }
  }

  showBuyerIdsDetail(buyerIds) {
    this.dialogBuyerIds = buyerIds;
    this.showBuyerIdsDialog = true;
  }

  showShopNamesDetail(shopNames) {
    this.dialogShopNames = shopNames;
    this.showShopNamesDialog = true;
  }

  handleOpenBillSendRecordsDialog(row) {
    this.currentBillSendRecordCustomer = {
      id: row.id,
      name: row.name,
    };
    this.showBillSendRecordsDialog = true;
  }

  handleBillSendRecordsUpdated() {
    if (this.$refs.sourcePage) {
      this.$refs.sourcePage.fetchData();
    }
  }

  async handleFetchData({ type }) {
    if (type === 'index') {
      this.summaryDataFetching = true;
      const params = this.transformIndexQueryParams({ ...this.$route.query });
      console.log(params)
      const [{ data, pagination }, summaryData] = await Promise.all([
        this.$request.get('/reports/receivables', { params }),
        this.$request.get('/reports/receivables/summary', { params })
      ]);
      this.summaryData = summaryData;
      this.summaryDataFetching = false;

      return {
        data,
        pagination
      }
    }

    if (type === 'show' || type === 'edit') {
      const data = await this.$request.get(`/reports/receivables/${this.$route.params.id}`);
      return { data };
    }

    if (type === 'new') {
      return {}
    }
  }
}


export const route = [
  {
    path: '/reports/receivables',
    name: 'reports/receivables.index',
    component: Receivables,
    props: {
      type: 'index',
    },
    meta: {
      can: 'reports/receivables.read',
    }
  },
  {
    path: '/reports/receivables/new',
    name: 'reports/receivables.new',
    component: Receivables,
    props: {
      type: 'new',
    },
    meta: {
      can: 'reports/receivables.create'
    }
  },
  {
    path: '/reports/receivables/:id',
    name: 'reports/receivables.show',
    component: Receivables,
    props: {
      type: 'show',
    },
    meta: {
      can: 'reports/receivables.read'
    }
  },
  {
    path: '/reports/receivables/:id/edit',
    name: 'reports/receivables.edit',
    component: Receivables,
    props: {
      type: 'edit'
    },
    meta: {
      can: 'reports/receivables.update'
    }
  }
]
</script>

<style lang="scss" scoped>
.receivables-page {
  display: flex;
  flex-direction: column;
  height: 100%;

  .receivables-page-header {
    padding: 0 20px;
    margin-top: 15px;
  }

  .receivables-page-main {
    flex: 1;
    overflow: hidden;
  }
}

.text-primary {
  color: $primary;
}

.card-container {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.summary-card {
  flex: 1;

  :deep(.el-card__header) {
    padding: 8px 15px;
    font-size: 13px;
    color: #fff;
  }

  &:nth-child(1) :deep(.el-card__header) {
    background-color: #e6a23c;
  }

  &.purple :deep(.el-card__header) {
    background-color: #8e44ad;
  }

  &.green :deep(.el-card__header) {
    background-color: #27ae60;
  }

  &.red :deep(.el-card__header) {
    background-color: #c0392b;
  }

  .amount {
    font-size: 20px;
    font-weight: bold;
    text-align: center;
  }
}

.buyer-ids-content {
  max-height: 400px;
  overflow-y: auto;

  .buyer-ids-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.shop-names-content {
  max-height: 400px;
  overflow-y: auto;

  .shop-names-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.dialog-footer {
  text-align: center;
}
</style>
